import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useTheme } from 'next-themes';
import { AppLayout } from '@/components/layout/AppLayout';
import ProtectedRoute from '@/components/ProtectedRoute';
import { ReloadPrompt } from '@/components/ReloadPrompt';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { NotificationModal } from '@/components/NotificationModal';
import { useDeviceType } from '@/hooks/useDeviceType';

// User Pages
import AuthPage from '@/pages/AuthPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DashboardPage from '@/pages/DashboardPage';
import ProfilePage from '@/pages/ProfilePage';
import ProfileEditPage from '@/pages/ProfileEditPage';
import PrivacySettingsPage from '@/pages/PrivacySettingsPage';
import ConnectionsPage from '@/pages/ConnectionsPage';
import SchedulePage from '@/pages/SchedulePage';
import MySchedulePage from '@/pages/MySchedulePage';
import MessagesPage from '@/pages/MessagesPage';
import ChatPage from '@/pages/ChatPage';
import ScannerPage from '@/pages/ScannerPage';
import OpportunitiesPage from '@/pages/OpportunitiesPage';
import SubmitOpportunityPage from '@/pages/SubmitOpportunityPage';
import IndustryPage from '@/pages/IndustryPage';
import EventMapPage from '@/pages/EventMapPage';
import SettingsPage from '@/pages/SettingsPage';
import RegisterUpdatePage from '@/pages/RegisterUpdatePage';
import RegisterLandingPage from '@/pages/RegisterLandingPage';
import RegisterExhibitorPage from '@/pages/RegisterExhibitorPage';
import RegisterNpsProjectPage from '@/pages/RegisterNpsProjectPage';
import RegisterAttendeePage from '@/pages/RegisterAttendeePage';

// Admin Pages
import AdminDashboard from '@/pages/admin/AdminDashboard';
import SessionManagement from '@/pages/admin/SessionManagement';
import SessionForm from '@/pages/admin/SessionForm';
import UserManagement from '@/pages/admin/UserManagement';
import Analytics from '@/pages/admin/Analytics';
import DataPage from '@/pages/admin/DataPage';
import AuditLogsPage from '@/pages/admin/AuditLogsPage';
import ProjectInterestsPage from '@/pages/admin/ProjectInterestsPage';
import SessionRsvpsPage from '@/pages/admin/SessionRsvpsPage';
import ProjectsPage from '@/pages/admin/ProjectsPage';
import NotificationsPage from '@/pages/admin/NotificationsPage';
import EventManagementPage from '@/pages/admin/EventManagementPage';
import SponsorManagementPage from '@/pages/admin/SponsorManagementPage';
import PartnerManagementPage from '@/pages/admin/PartnerManagementPage';
import BadgeExportPage from '@/pages/admin/BadgeExportPage';

// Staff Pages
import StaffCheckinPage from '@/pages/staff/StaffCheckinPage';

// On desktop the settings modal is used instead of this full-page route
function SettingsRoute() {
  const deviceType = useDeviceType();
  if (deviceType === 'desktop') {
    return <Navigate to="/" replace />;
  }
  return <SettingsPage />;
}

function App() {
  const { theme } = useTheme();
  usePushNotifications();

  return (
    <NotificationProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/register" element={<RegisterLandingPage />} />
        <Route path="/register/exhibitor" element={<RegisterExhibitorPage />} />
        <Route path="/register/nps-project" element={<RegisterNpsProjectPage />} />
        <Route path="/register/attendee" element={<RegisterAttendeePage />} />
        <Route path="/register/update/:token" element={<RegisterUpdatePage />} />

        {/* Protected user routes with bottom nav */}
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/edit" element={<ProfileEditPage />} />
          <Route path="/privacy-settings" element={<PrivacySettingsPage />} />
          <Route path="/scanner" element={<ScannerPage />} />
          <Route path="/network" element={<ConnectionsPage />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/my-schedule" element={<MySchedulePage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/messages/:conversationId" element={<ChatPage />} />
          <Route path="/opportunities" element={<OpportunitiesPage />} />
          <Route path="/opportunities/submit" element={<SubmitOpportunityPage />} />
          <Route path="/industry-partners" element={<IndustryPage />} />
          <Route path="/event-map" element={<EventMapPage />} />
          <Route path="/settings" element={<SettingsRoute />} />
        </Route>

        {/* Admin routes (require admin/staff role) */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/sessions"
          element={
            <ProtectedRoute requireAdmin>
              <SessionManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/sessions/new"
          element={
            <ProtectedRoute requireAdmin>
              <SessionForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/sessions/:id/edit"
          element={
            <ProtectedRoute requireAdmin>
              <SessionForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/network"
          element={
            <ProtectedRoute requireAdmin>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute requireAdmin>
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/data"
          element={
            <ProtectedRoute requireAdmin>
              <DataPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit-logs"
          element={
            <ProtectedRoute requireAdmin>
              <AuditLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/opportunities"
          element={
            <ProtectedRoute requireAdmin>
              <ProjectsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/partners"
          element={
            <ProtectedRoute requireAdmin>
              <PartnerManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/notifications"
          element={
            <ProtectedRoute requireAdmin>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/events"
          element={
            <ProtectedRoute requireAdmin>
              <EventManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/sponsors"
          element={
            <ProtectedRoute requireAdmin>
              <SponsorManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/badges"
          element={
            <ProtectedRoute requireAdmin>
              <BadgeExportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/opportunities/:id/interests"
          element={
            <ProtectedRoute requireAdmin>
              <ProjectInterestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/sessions/:id/rsvps"
          element={
            <ProtectedRoute requireAdmin>
              <SessionRsvpsPage />
            </ProtectedRoute>
          }
        />

        {/* Staff routes (require staff/admin role) */}
        <Route
          path="/staff/checkin"
          element={
            <ProtectedRoute requireStaff>
              <StaffCheckinPage />
            </ProtectedRoute>
          }
        />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster
        position="top-center"
        theme={theme as 'light' | 'dark' | 'system'}
        toastOptions={{
          className: 'sonner-custom',
        }}
      />
      <ReloadPrompt />
      <NotificationModal />
    </NotificationProvider>
  );
}

export default App;
